import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ChevronDown, ChevronUp, CheckCheck, Sparkles, PartyPopper, Send, Percent, X, CheckSquare,
} from 'lucide-react';
import { useTheme, money } from '../lib/theme';
import {
  getScore, getBookkeeperProgress, listBookkeeperQueue,
  acceptReview, patchReceipt, getBuckets, getCategories, postTaxReview,
  bulkApprove, bulkCategorize, bulkClassifyAsset, bulkDelete,
  bulkMarkDuplicate, bulkTaxCode, bulkTaxCodeV2,
  bulkMarkReviewed, bulkMarkTaxReviewed,
} from '../lib/api';
import { groupByBucket } from '../lib/categoryGrouping';
import ScoreCard from '../components/ScoreCard';
import BulkActionsBar from '../components/receipts/BulkActionsBar';
import BulkPublishModal from '../components/quickbooks/BulkPublishModal';
import ConfirmDialog from '../components/ConfirmDialog';

const FILTER_LABEL = {
  not_reviewed: 'Not Reviewed',
  reviewed: 'Reviewed',
  needs_attention: 'Needs Attention',
  all: 'All',
};

const PAGE = 25;

function ConfPill({ label, value, colors }) {
  if (value == null) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold border"
        style={{ backgroundColor: colors.surface, color: colors.textMuted, borderColor: colors.border }}
        data-testid={`pill-${label.toLowerCase()}`}>
        <span>{label}</span><span className="tabular-nums">—</span>
      </span>
    );
  }
  const tone = value >= 0.85 ? 'good' : value >= 0.6 ? 'mid' : 'bad';
  const palette = {
    good: { bg: '#DCFCE7', fg: '#166534' },
    mid:  { bg: '#FEF3C7', fg: '#92400E' },
    bad:  { bg: '#FEE2E2', fg: '#991B1B' },
  }[tone];
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold tabular-nums"
      style={{ backgroundColor: palette.bg, color: palette.fg }}
      data-testid={`pill-${label.toLowerCase()}`}>
      <span>{label}</span>
      <span>{Math.round(value * 100)}%</span>
    </span>
  );
}

function EditRow({ colors, item, categories, buckets, onFieldPatch, onAcceptReview, onOpenDetail, onMarkTaxReviewed }) {
  const [vendor, setVendor] = useState(item.vendor || '');
  const [date, setDate] = useState(item.date || '');
  const [subtotal, setSubtotal] = useState(item.subtotal != null ? String(item.subtotal) : '');
  const [gst, setGst] = useState(item.gst != null ? String(item.gst) : '');
  const [qst, setQst] = useState(item.qst != null ? String(item.qst) : '');
  const [totalStr, setTotalStr] = useState(item.total != null ? String(item.total) : '');
  const [catOpen, setCatOpen] = useState(false);
  const [catQuery, setCatQuery] = useState('');

  const commitVendor = () => {
    if (vendor === (item.vendor || '')) return;
    onFieldPatch(item, { vendor: { value: vendor.trim() || null, confidence: 1.0 } });
  };
  const commitDate = () => {
    if (date === (item.date || '')) return;
    onFieldPatch(item, { date: { value: date || null, confidence: 1.0 } });
  };
  const commitNum = (key, s, existing) => {
    if (s === (existing != null ? String(existing) : '')) return;
    const n = s.trim() === '' ? null : Number(s);
    if (s.trim() !== '' && isNaN(n)) return;
    onFieldPatch(item, { [key]: { value: n, confidence: 1.0 } });
  };
  const commitCategory = (name) => {
    setCatOpen(false); setCatQuery('');
    onFieldPatch(item, { category: name, category_confirmed: true });
  };

  const isLow = (v) => v != null && v < 0.6;

  const inputStyle = (low) => ({
    backgroundColor: colors.bg,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderLeft: low ? `3px solid ${colors.warning}` : `1px solid ${colors.border}`,
  });

  const c = item.field_confidences;

  return (
    <div className="mt-3 pt-3 border-t space-y-3" style={{ borderColor: colors.border }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>Vendor</label>
            <ConfPill label="V" value={c.vendor} colors={colors} />
          </div>
          <input
            data-testid={`edit-vendor-${item.receipt_id}`}
            value={vendor} onChange={(e) => setVendor(e.target.value)} onBlur={commitVendor}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle(isLow(c.vendor))} placeholder="Vendor" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>Date</label>
            <ConfPill label="D" value={c.date} colors={colors} />
          </div>
          <input
            data-testid={`edit-date-${item.receipt_id}`}
            type="date" value={date} onChange={(e) => setDate(e.target.value)} onBlur={commitDate}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={inputStyle(isLow(c.date))} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ['Subtotal', 'ST', subtotal, setSubtotal, c.subtotal, item.subtotal, 'subtotal'],
          ['GST',      'GST', gst,      setGst,      c.gst,      item.gst,      'gst'],
          ['QST',      'QST', qst,      setQst,      c.qst,      item.qst,      'qst'],
          ['Total',    'TOT', totalStr, setTotalStr, c.total,    item.total,    'total'],
        ].map(([label, pillLabel, val, setter, conf, orig, key]) => (
          <div key={key}>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>{label}</label>
              <ConfPill label={pillLabel} value={conf} colors={colors} />
            </div>
            <input
              data-testid={`edit-${key}-${item.receipt_id}`}
              inputMode="decimal" value={val} onChange={(e) => setter(e.target.value)}
              onBlur={() => commitNum(key, val, orig)}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none text-right tabular-nums"
              style={inputStyle(isLow(conf))} placeholder="0.00" />
          </div>
        ))}
      </div>

      {/* Category */}
      <div className="relative">
        <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: colors.textMuted }}>Category</label>
        <button
          data-testid={`edit-category-${item.receipt_id}`}
          onClick={() => setCatOpen((v) => !v)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm border"
          style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }}>
          <span>{item.category || 'Pick a category'}</span>
          <ChevronDown size={14} style={{ color: colors.textMuted }} />
        </button>
        {catOpen ? (
          <div className="absolute z-10 mt-1 w-full rounded-lg border shadow-lg max-h-60 overflow-y-auto"
            style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
            <input autoFocus value={catQuery} onChange={(e) => setCatQuery(e.target.value)}
              placeholder="Search…"
              className="w-full px-3 py-2 text-sm outline-none border-b"
              style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }} />
            {(() => {
              const query = catQuery.trim().toLowerCase();
              const filtered = query
                ? categories.filter((cc) => cc.name.toLowerCase().includes(query))
                : categories;
              return groupByBucket(filtered, buckets).map((row) => (
                <div key={row.bucket}>
                  <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 sticky top-[45px]"
                    style={{ backgroundColor: colors.surface }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.color }} />
                    <span className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: colors.textMuted }}
                    >{row.label}</span>
                  </div>
                  {row.categories.map((cc) => (
                    <button key={cc.id}
                      data-testid={`cat-option-${cc.name}`}
                      onClick={() => commitCategory(cc.name)}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-left text-sm hover:opacity-80"
                      style={{ color: colors.text }}>
                      <span>{cc.name}</span>
                    </button>
                  ))}
                </div>
              ));
            })()}
          </div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <button
          data-testid={`accept-review-${item.receipt_id}`}
          onClick={onAcceptReview}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
          style={{ backgroundColor: colors.accent }}>
          <CheckCheck size={14} /> Accept as reviewed
        </button>
        {onMarkTaxReviewed ? (
          <button
            data-testid={`mark-tax-reviewed-${item.receipt_id}`}
            onClick={onMarkTaxReviewed}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
            title="Dismiss the tax discrepancy for this receipt">
            <Percent size={14} /> Mark tax reviewed
          </button>
        ) : null}
        <button
          data-testid={`full-detail-${item.receipt_id}`}
          onClick={onOpenDetail}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold border"
          style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}>
          Full detail <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

function QueueRow({ colors, item, expanded, onToggle, onAcceptReview, onFieldPatch, categories, buckets, onOpenDetail, onMarkTaxReviewed, selectMode, isSelected, onToggleSelect }) {
  const isHigh = item.priority_highest === 'high';
  const taxIssue = (item.issues || []).find((i) => i.type === 'tax_discrepancy');
  return (
    <div
      data-testid={`queue-row-${item.receipt_id}`}
      className="rounded-xl border p-4 transition-colors"
      style={{
        backgroundColor: colors.surface,
        borderColor: selectMode && isSelected ? colors.accent : colors.border,
        borderWidth: selectMode && isSelected ? 2 : 1,
        borderLeft: isHigh ? `3px solid ${colors.error}` : undefined,
      }}>
      <button
        onClick={selectMode ? onToggleSelect : onToggle}
        className="w-full flex items-start gap-3 text-left"
      >
        {selectMode ? (
          <div
            data-testid={`bk-checkbox-${item.receipt_id}`}
            className="w-5 h-5 rounded flex items-center justify-center border shrink-0 mt-1"
            style={{
              backgroundColor: isSelected ? colors.accent : colors.bg,
              borderColor: isSelected ? colors.accent : colors.border,
            }}
          >
            {isSelected ? <CheckCheck size={12} color="#FFFFFF" /> : null}
          </div>
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold truncate" style={{ color: colors.text }}>
              {item.vendor || 'Unknown vendor'}
            </span>
            {isHigh ? (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-black text-white"
                style={{ backgroundColor: colors.error }}
                data-testid={`priority-high-${item.receipt_id}`}>!</span>
            ) : null}
          </div>
          <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
            {item.date || 'No date'}
            {' · '}
            <span style={{ color: item.category ? colors.text : colors.textMuted }}>
              {item.category || 'Uncategorized'}
            </span>
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-base font-semibold tabular-nums" style={{ color: colors.text }}>
            {item.total != null ? money(item.total) : '—'}
          </div>
        </div>
      </button>

      <div className="flex items-center gap-1.5 mt-3 flex-wrap">
        <ConfPill label="V"   value={item.field_confidences.vendor}   colors={colors} />
        <ConfPill label="D"   value={item.field_confidences.date}     colors={colors} />
        <ConfPill label="ST"  value={item.field_confidences.subtotal} colors={colors} />
        <ConfPill label="GST" value={item.field_confidences.gst}      colors={colors} />
        <ConfPill label="QST" value={item.field_confidences.qst}      colors={colors} />
        <ConfPill label="TOT" value={item.field_confidences.total}    colors={colors} />
        {taxIssue ? (
          <span
            data-testid={`tax-mismatch-chip-${item.receipt_id}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
            style={{
              backgroundColor: taxIssue.priority === 'high' ? '#FEE2E2' : '#FEF3C7',
              color: taxIssue.priority === 'high' ? '#991B1B' : '#92400E',
              borderColor: taxIssue.priority === 'high' ? '#FCA5A5' : '#FCD34D',
            }}>
            <Percent size={10} /> Tax mismatch
          </span>
        ) : null}
        {item.qb_publish?.status === 'published' && (
          <span
            data-testid={`qb-chip-${item.receipt_id}`}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold"
            style={{ backgroundColor: '#E7F7EE', color: '#117A3C' }}
            title={`Published to QuickBooks on ${item.qb_publish.published_at?.slice(0, 10) || ''}`}
          >
            QB
          </span>
        )}
        <button onClick={onToggle} className="ml-auto" style={{ color: colors.textMuted }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {expanded && !selectMode ? (
        <EditRow
          colors={colors} item={item} categories={categories} buckets={buckets}
          onFieldPatch={onFieldPatch}
          onAcceptReview={onAcceptReview}
          onOpenDetail={onOpenDetail}
          onMarkTaxReviewed={taxIssue ? onMarkTaxReviewed : null}
        />
      ) : null}
    </div>
  );
}

export default function Bookkeeper() {
  const { colors, effective } = useTheme();
  const navigate = useNavigate();

  const [score, setScore] = useState(null);
  const [progress, setProgress] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('not_reviewed');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [buckets, setBuckets] = useState([]);

  // Phase 19 — selection state (ids-only in Bookkeeper Mode; the filtered
  // queue is a curated review list so "all in view" is intentionally omitted).
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkPublishOpen, setBulkPublishOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((msg, duration = 3000) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), duration);
  }, []);
  const toggleSelect = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const clearSelect = useCallback(() => {
    setSelected(new Set()); setSelectMode(false);
  }, []);
  const selectAllPage = useCallback(() => {
    setSelected(new Set(items.map((it) => it.receipt_id)));
  }, [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, q] = await Promise.all([
        getScore().catch(() => null),
        getBookkeeperProgress().catch(() => null),
        listBookkeeperQueue({ filter, limit: PAGE, offset: 0 }),
      ]);
      setScore(s); setProgress(p);
      setItems(q.items); setTotal(q.total);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    getCategories()
      .then((r) => setCategories(r.categories.filter((c) => !c.is_special)))
      .catch(() => setCategories([]));
    getBuckets().then(setBuckets).catch(() => setBuckets([]));
  }, []);

  const onAcceptReview = async (id) => {
    if (filter === 'not_reviewed') {
      setItems((prev) => prev.filter((it) => it.receipt_id !== id));
      setTotal((v) => Math.max(0, v - 1));
      setExpandedId(null);
    }
    try {
      await acceptReview(id);
      const [s, p] = await Promise.all([
        getScore().catch(() => null),
        getBookkeeperProgress().catch(() => null),
      ]);
      setScore(s); setProgress(p);
      if (filter !== 'not_reviewed') { void load(); }
    } catch {
      void load();
    }
  };

  const onFieldPatch = async (item, patch) => {
    setItems((prev) => prev.map((it) => {
      if (it.receipt_id !== item.receipt_id) return it;
      const next = { ...it };
      if (patch.vendor?.value !== undefined) next.vendor = patch.vendor.value ?? null;
      if (patch.date?.value !== undefined) next.date = patch.date.value ?? null;
      if (patch.subtotal?.value !== undefined) next.subtotal = patch.subtotal.value ?? null;
      if (patch.gst?.value !== undefined) next.gst = patch.gst.value ?? null;
      if (patch.qst?.value !== undefined) next.qst = patch.qst.value ?? null;
      if (patch.total?.value !== undefined) next.total = patch.total.value ?? null;
      if (patch.category !== undefined) {
        next.category = patch.category;
        next.category_confirmed = !!patch.category;
      }
      return next;
    }));
    try {
      await patchReceipt(item.receipt_id, patch);
    } catch {
      void load();
    }
  };

  // Phase 17 — dismiss a tax_discrepancy issue for a single receipt.
  const onMarkTaxReviewed = async (id) => {
    // Optimistic: drop the tax_discrepancy issue from the item locally.
    setItems((prev) => prev.map((it) => it.receipt_id === id
      ? { ...it, issues: (it.issues || []).filter((i) => i.type !== 'tax_discrepancy') }
      : it,
    ));
    try {
      await postTaxReview(id, true);
      const s = await getScore().catch(() => null);
      if (s) setScore(s);
    } catch {
      void load();
    }
  };

  const filterCounts = useMemo(() => {
    const nr = progress?.not_reviewed ?? 0;
    const rv = progress?.reviewed ?? 0;
    const attn = (score?.issues?.high ?? 0) + (score?.issues?.medium ?? 0);
    const all = progress?.total ?? 0;
    return { not_reviewed: nr, reviewed: rv, needs_attention: attn, all };
  }, [progress, score]);

  // Phase 19 — bulk action runner.
  const runBulk = useCallback(async (fn, verb) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      const r = await fn({ ids });
      const count = r?.updated ?? r?.matched ?? 0;
      const msg = r?.summary || `${count} receipt${count !== 1 ? 's' : ''} ${verb}`;
      showToast(msg);
      clearSelect();
      await load();
    } catch (err) {
      console.error('Bulk action failed:', err);
      showToast(`Bulk ${verb} failed`);
    }
  }, [selected, showToast, clearSelect, load]);

  const doApprove = useCallback(() => runBulk((sel) => bulkApprove(sel, 'approved'), 'approved'), [runBulk]);
  const doDelete = useCallback(() => setConfirmDelete(true), []);
  const runDeleteConfirmed = useCallback(() => {
    setConfirmDelete(false);
    runBulk((sel) => bulkDelete(sel), 'deleted');
  }, [runBulk]);
  const doMarkDuplicate = useCallback(() => runBulk((sel) => bulkMarkDuplicate(sel, true), 'marked duplicate'), [runBulk]);
  const doClassifyAsset = useCallback(
    (isAsset) => runBulk((sel) => bulkClassifyAsset(sel, isAsset), isAsset ? 'flagged as asset' : 'classified as expense'),
    [runBulk],
  );
  const doCategorize = useCallback(
    (name) => runBulk((sel) => bulkCategorize(sel, name), `→ ${name}`),
    [runBulk],
  );
  const doTaxCode = useCallback(
    (key) => runBulk((sel) => bulkTaxCode(sel, key), key ? `tax code → ${key}` : 'tax override cleared'),
    [runBulk],
  );
  const doTaxCodeV2 = useCallback(
    (tc) => {
      const label = tc?.no_tax_applicable
        ? 'no tax'
        : [tc?.federal_code, tc?.provincial_code].filter(Boolean).join(' + ') || 'tax override cleared';
      return runBulk((sel) => bulkTaxCodeV2(sel, tc), `tax code → ${label}`);
    },
    [runBulk],
  );
  const doMarkReviewed = useCallback(
    () => runBulk((sel) => bulkMarkReviewed({ ...sel, reviewed: true }), 'marked as reviewed'),
    [runBulk],
  );
  const doMarkTaxReviewedBulk = useCallback(
    () => runBulk((sel) => bulkMarkTaxReviewed({ ...sel, reviewed: true }), 'marked tax reviewed'),
    [runBulk],
  );

  const isComplete = !!progress?.complete;
  const percentage = progress?.percentage ?? 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-testid="bookkeeper-page">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: colors.text }}>Bookkeeper Mode</h1>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Review your receipts to raise your Clasifi Score.</p>
        </div>
        {selectMode ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold" style={{ color: colors.text }}>
              {selected.size} selected
            </span>
            <button
              data-testid="bk-select-all-page-btn"
              onClick={selectAllPage}
              className="text-xs font-bold px-3 py-1.5 rounded-lg border"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
            >
              Select page ({items.length})
            </button>
            <button
              data-testid="bk-select-close"
              onClick={clearSelect}
              className="p-1.5 rounded-lg border"
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            data-testid="bk-enter-select-btn"
            onClick={() => setSelectMode(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
          >
            <CheckSquare size={14} /> Select
          </button>
        )}
      </div>

      {score ? (
        <div className="mb-4">
          <ScoreCard score={score} colors={colors} dark={effective === 'dark'} compact linkTo={null} progress={progress} />
        </div>
      ) : null}

      {isComplete ? (
        <div className="rounded-xl border p-8 text-center mb-6 flex flex-col items-center gap-2"
          style={{ backgroundColor: colors.successSoft, borderColor: colors.success }}
          data-testid="celebration-card">
          <PartyPopper size={40} style={{ color: colors.success }} />
          <p className="text-xl font-bold" style={{ color: colors.text }}>Your books are ready for the next step.</p>
          <p className="text-sm" style={{ color: colors.textMuted }}>
            {progress.total} receipts, all reviewed and accounted for.
          </p>
          <button
            data-testid="submit-to-accountant-btn"
            onClick={() => navigate('/reporting')}
            className="mt-2 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: colors.accent }}>
            <Send size={14} /> Submit to Accountant
          </button>
        </div>
      ) : (
        <div className="rounded-xl border p-4 mb-4" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>
              Review progress
            </span>
            <span className="text-sm font-semibold tabular-nums" style={{ color: colors.text }} data-testid="progress-stat">
              {progress?.reviewed ?? 0} / {progress?.total ?? 0} ({percentage.toFixed(1)}%)
            </span>
          </div>
          <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}>
            <div className="h-full rounded-full transition-all"
              data-testid="progress-fill"
              style={{ width: `${Math.min(100, percentage)}%`, backgroundColor: colors.success }} />
          </div>
          <div className="flex justify-end mt-2">
            <button
              data-testid="submit-to-accountant-link"
              onClick={() => navigate('/reporting')}
              className="inline-flex items-center gap-1 text-sm font-bold"
              style={{ color: colors.accent }}>
              Submit to Accountant <ArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="issue-filters">
        {Object.keys(FILTER_LABEL).map((f) => {
          const active = filter === f;
          return (
            <button key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors"
              style={{
                backgroundColor: active ? colors.accentSoft : colors.surface,
                borderColor: active ? colors.accent : colors.border,
                color: active ? colors.accent : colors.text,
              }}>
              {FILTER_LABEL[f]}
              <span className="tabular-nums opacity-80">({filterCounts[f]})</span>
            </button>
          );
        })}
      </div>

      {/* Queue */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 rounded-full animate-spin"
            style={{ borderColor: colors.border, borderTopColor: colors.accent }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-xl border border-dashed"
          data-testid="queue-empty"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
          <Sparkles size={40} style={{ color: colors.accent }} />
          <p className="text-lg font-bold mt-3" style={{ color: colors.text }}>
            {filter === 'not_reviewed' ? 'All caught up on this filter' : 'Nothing to show'}
          </p>
          <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Try another filter.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="queue-list">
          {items.map((it) => (
            <QueueRow
              key={it.receipt_id}
              colors={colors}
              item={it}
              expanded={expandedId === it.receipt_id}
              onToggle={() => setExpandedId((v) => v === it.receipt_id ? null : it.receipt_id)}
              onAcceptReview={() => onAcceptReview(it.receipt_id)}
              onFieldPatch={onFieldPatch}
              categories={categories}
              buckets={buckets}
              onOpenDetail={() => navigate(`/receipts/${it.receipt_id}`)}
              onMarkTaxReviewed={() => onMarkTaxReviewed(it.receipt_id)}
              selectMode={selectMode}
              isSelected={selected.has(it.receipt_id)}
              onToggleSelect={() => toggleSelect(it.receipt_id)}
            />
          ))}
          {total > items.length ? (
            <p className="text-center text-xs mt-4" style={{ color: colors.textMuted }}>
              Showing {items.length} of {total}. Refresh or narrow filters for more.
            </p>
          ) : null}
        </div>
      )}

      {/* Phase 19 — Bulk actions bar (sticky floating at bottom) */}
      {selectMode && selected.size > 0 ? (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 shadow-lg"
          style={{ backgroundColor: colors.surface }}
        >
          <BulkActionsBar
            colors={colors}
            context="bookkeeper"
            onApprove={doApprove}
            onDelete={doDelete}
            onMarkDuplicate={doMarkDuplicate}
            onClassifyAsset={doClassifyAsset}
            onCategorize={doCategorize}
            onTaxCode={doTaxCode}
            onTaxCodeV2={doTaxCodeV2}
            onMarkReviewed={doMarkReviewed}
            onMarkTaxReviewed={doMarkTaxReviewedBulk}
            onPublishToQb={() => setBulkPublishOpen(true)}
            countLabel={`${selected.size} receipt${selected.size === 1 ? '' : 's'}`}
          />
        </div>
      ) : null}

      {/* Phase 25.3 — Bulk publish modal */}
      <BulkPublishModal
        open={bulkPublishOpen}
        onClose={() => setBulkPublishOpen(false)}
        receiptIds={Array.from(selected)}
        onDone={() => { void load(); }}
      />

      {toast ? (
        <div
          className="fixed bottom-24 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full text-sm font-semibold text-white z-50 shadow-lg"
          style={{ backgroundColor: colors.text }}
          data-testid="bookkeeper-toast"
        >
          {toast}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete ${selected.size} receipt${selected.size === 1 ? '' : 's'}?`}
        body="This can't be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={runDeleteConfirmed}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
